import React, {useCallback, useEffect} from 'react';
import {inject, observer} from 'mobx-react';
import {CurrentTimeStore} from 'stores/current_time_store';
import {REPEATABILITY_INFINITE, REPEATABILITY_ONE_TIME, SEC_IN_DAY, SEC_IN_HOUR} from 'app/utils/Consts';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import I18n from 'app/i18n';
import {toastInfo} from 'app/components/toasts/liteToast';
import type PromoNewItemInterface from 'app/interfaces/articles/PromoNewItemInterface';

interface PromoNewCardTimerProps {
    promoType: PromoNewItemInterface['promo_type']
    repeatability?: string
    startTime?: number | null
    endTime?: number | null
    onExpire: () => void
    currentTimeStore?: CurrentTimeStore
}

const formatRemainingTime = (secondsRemaining: number, longForm = false): string => {
    const isDays = secondsRemaining >= SEC_IN_DAY;
    const daysRemaining = Math.floor(secondsRemaining / SEC_IN_DAY);
    const hoursRemaining = Math.floor(secondsRemaining / SEC_IN_HOUR);

    if (longForm) {
        if (isDays) {
            return I18n.t('days', {count: daysRemaining}).toLowerCase();
        }

        return I18n.t('hours', {count: hoursRemaining || 1}).toLowerCase();
    }

    if (isDays) {
        return `${daysRemaining} ${I18n.t('days_short')}`;
    }

    return `${hoursRemaining || 1} ${I18n.t('hours_short')}`;
};

const getTimerMessage = (isStartCountdown: boolean, time: string, isToast = false): string => {
    const keyType = isToast ? 'toast' : 'label';
    const keyPhase = isStartCountdown ? 'start' : 'end';

    return I18n.t(`promo_new_${keyType}_time_to_${keyPhase}`, {time});
};

const PromoNewCardCadence = ({repeatability}: {repeatability?: string}): React.ReactElement | null => {
    if (repeatability === REPEATABILITY_ONE_TIME || repeatability === REPEATABILITY_INFINITE) {
        return null;
    }

    return <div className="promo-new-card__timer">
        <SpriteIcon name="#svg-timer-icon" size={{height: 16, width: 16}} />
        <span className="promo-new-card__timer-text">
            {I18n.t(`promo_new_cadence_${repeatability ?? 'permanent'}`)}
        </span>
    </div>;
};

const PromoNewCardTimer = ({
    promoType,
    repeatability,
    startTime,
    endTime,
    onExpire,
    currentTimeStore
}: PromoNewCardTimerProps): React.ReactElement | null => {
    if (promoType === 'permanent') {
        return <PromoNewCardCadence repeatability={repeatability} />;
    }

    const currentTime = currentTimeStore?.t ?? 0;
    const isStartCountdown = Boolean(startTime && startTime > currentTime);
    const targetTime = isStartCountdown
        ? startTime
        : endTime;
    const secondsRemaining = (targetTime ?? 0) - currentTime;
    const isEndExpired = Boolean(!isStartCountdown && endTime && currentTimeStore && secondsRemaining < 0);
    const remainingTime = formatRemainingTime(secondsRemaining);
    const remainingTimeLongForm = formatRemainingTime(secondsRemaining, true);
    const timerClassName = `promo-new-card__timer ${isStartCountdown ? 'promo-new-card__timer--starts' : ''}`;

    useEffect(() => {
        if (isEndExpired) {
            onExpire();
        }
    }, [isEndExpired, onExpire]);

    const handleTimerClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        toastInfo(getTimerMessage(isStartCountdown, remainingTimeLongForm, true));
    }, [remainingTimeLongForm, isStartCountdown]);

    if (!targetTime || !currentTimeStore || isEndExpired) {
        return null;
    }

    return (
        <button
            type="button"
            className={timerClassName}
            aria-label={getTimerMessage(isStartCountdown, remainingTime)}
            onClick={handleTimerClick}
        >
            <SpriteIcon name="#svg-timer-icon" size={{height: 16, width: 16}} />
            <span className="promo-new-card__timer-text">
                {remainingTime}
            </span>
        </button>
    );
};

export default inject('currentTimeStore')(observer(PromoNewCardTimer));
